import React from 'react';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { ChipsContainer } from 'app/ComponentLibrary/Filter';
import { generateFieldChips } from 'app/components/AmWorkDesk/PlacementsDesk/filterHelper';
import { calculateParentPath, findBottomNode, injectItem } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';
import { myCandidatesActions } from './MyCandidates.redux';
import { recCandidatesFilterName } from './WorkdeskFilter';

interface ICandidatesChipsProps {
  onApplyFilter: (values: Record<string, unknown>) => void;
}

export const CandidatesChips = ({ onApplyFilter }: ICandidatesChipsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getValues, setValue, watch } = useFormContext();

  const placementRecruiter = watch('placementRecruiter');
  const discipline = watch('discipline') as { id: number; name: string; value: string }[] | undefined;
  const specialty = watch('specialty') as { id: number; name: string; value: string }[] | undefined;
  const states = watch('states');
  const candidateStatus = watch('candidateStatus') as { id: number; name: string; value: string }[] | undefined;
  const rebooked = watch('rebooked');
  const bookedOutOfRange = watch('bookedOutOfRange');
  const placementStatus = watch('placementStatus') as any[] | undefined;

  const recruiter = Array.isArray(placementRecruiter)
    ? placementRecruiter[0]
    : placementRecruiter && typeof placementRecruiter === 'object'
      ? placementRecruiter
      : null;

  const chips = React.useMemo(() => {
    const result: Record<string, any[]> = {};
    if (recruiter?.name) {
      result.placementRecruiter = [
        {
          label: `${t('home.recruiterWorkDesk.placementFilter.placementRecruiter')} : ${recruiter.name}`,
          enableDelete: true,
          value: recruiter,
        },
      ];
    }
    const disciplineChips = (discipline || [])
      .filter((d: any) => d?.value !== 'All')
      .map((d: any) => ({
        label: `${t('home.recruiterWorkDesk.placementFilter.discipline')} : ${d.name}`,
        enableDelete: true,
        value: d,
      }));
    const specialtyChips = (specialty || [])
      .filter((s: any) => s?.value !== 'All')
      .map((s: any) => ({
        label: `${t('home.recruiterWorkDesk.placementFilter.specialty')} : ${s.name}`,
        enableDelete: true,
        value: s,
      }));
    const statesVal = states && typeof states === 'object' && 'value' in states ? (states as any).value : states;
    const statesLabel = states && typeof states === 'object' && 'name' in states ? (states as any).name : statesVal;
    if (statesVal && statesVal !== 'all') {
      result.states = [
        {
          label: `${t('home.recruiterWorkDesk.placementFilter.state')} : ${statesLabel || statesVal}`,
          enableDelete: true,
          value: states,
        },
      ];
    }
    const candidateStatusChips = (candidateStatus || [])
      .filter((cs: any) => cs?.value !== 'All')
      .map((cs: any) => ({
        label: `${t('search.globalSearch.candidate.gridTags.candidateStatus')} : ${cs.name}`,
        enableDelete: true,
        value: cs,
      }));
    const rebookedVal = rebooked && typeof rebooked === 'object' && 'value' in rebooked ? (rebooked as any).value : rebooked;
    if (rebookedVal && rebookedVal !== 'all') {
      result.rebooked = [
        {
          label: `Rebooked : ${rebookedVal === 'Y' ? 'Yes' : 'No'}`,
          enableDelete: true,
          value: rebooked,
        },
      ];
    }
    const bookedOutOfRangeVal =
      bookedOutOfRange && typeof bookedOutOfRange === 'object' && 'value' in bookedOutOfRange
        ? (bookedOutOfRange as any).value
        : bookedOutOfRange;
    if (bookedOutOfRangeVal && bookedOutOfRangeVal !== 'all') {
      result.bookedOutOfRange = [
        {
          label: `Booked out of range : ${bookedOutOfRangeVal === 'Y' ? 'Yes' : 'No'}`,
          enableDelete: true,
          value: bookedOutOfRange,
        },
      ];
    }
    const placementStatusChips =
      Array.isArray(placementStatus) && placementStatus.length > 0
        ? generateFieldChips(placementStatus, undefined, t('home.recruiterWorkDesk.placementFilter.placementStatus'))
        : [];
    if (disciplineChips.length > 0) result.discipline = disciplineChips;
    if (specialtyChips.length > 0) result.specialty = specialtyChips;
    if (candidateStatusChips.length > 0) result.candidateStatus = candidateStatusChips;
    if (placementStatusChips.length > 0) result.placementStatus = placementStatusChips;
    return result;
  }, [recruiter, discipline, specialty, states, candidateStatus, rebooked, bookedOutOfRange, placementStatus, t]);

  const handleChipDelete = (field: string, chip: any) => {
    const values = getValues();
    if (field === 'placementRecruiter') {
      setValue('placementRecruiter', null);
      dispatch(myCandidatesActions.setSelectedBarChartMonth(null));
      dispatch(myCandidatesActions.setRecruiterId(null));
      onApplyFilter({ ...values, placementRecruiter: null });
    } else if (field === 'discipline') {
      const deleteValue = chip?.value?.value ?? chip?.value;
      const next = (values.discipline || []).filter((d: any) => (d?.value ?? d) !== deleteValue);
      setValue('discipline', next);
      onApplyFilter({ ...values, discipline: next });
    } else if (field === 'specialty') {
      const deleteValue = chip?.value?.value ?? chip?.value;
      const next = (values.specialty || []).filter((s: any) => (s?.value ?? s) !== deleteValue);
      setValue('specialty', next);
      onApplyFilter({ ...values, specialty: next });
    } else if (field === recCandidatesFilterName.states) {
      setValue('states', 'all');
      onApplyFilter({ ...values, states: 'all' });
    } else if (field === recCandidatesFilterName.candidateStatus) {
      const deleteValue = chip?.value?.value ?? chip?.value;
      const next = (values.candidateStatus || []).filter((cs: any) => (cs?.value ?? cs) !== deleteValue);
      setValue('candidateStatus', next);
      onApplyFilter({ ...values, candidateStatus: next });
    } else if (field === recCandidatesFilterName.rebooked) {
      setValue('rebooked', 'all');
      onApplyFilter({ ...values, rebooked: 'all' });
    } else if (field === recCandidatesFilterName.bookedOutOfRange) {
      setValue('bookedOutOfRange', 'all');
      onApplyFilter({ ...values, bookedOutOfRange: 'all' });
    } else if (field === recCandidatesFilterName.placementStatus) {
      const fieldValues = values.placementStatus || [];
      const newFieldValues = injectItem(
        _.cloneDeep(fieldValues),
        calculateParentPath(chip.value),
        findBottomNode(chip.value),
        true,
        'value',
        'name',
      );
      setValue('placementStatus', newFieldValues);
      onApplyFilter({ ...values, placementStatus: newFieldValues });
    }
  };

  if (Object.keys(chips).length === 0) return null;

  return <ChipsContainer chips={chips} onClick={() => {}} onDelete={handleChipDelete} />;
};
